import React, { useState, useEffect, useCallback, useId } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  message,
  Card,
  Spin,
  Row,
  Col,
  Alert,
  Typography,
  Tabs,
  Grid,
  Pagination,
  Tooltip,
  Empty,
} from "antd";
import { useRef } from "react";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  EyeOutlined,
  DownloadOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import axios from "axios";
import apiClient from "../api/apiClient";
import dayjs from "dayjs";
import { useSelection } from "../SelectionContext";
import { useAuth } from "../context/AuthContext";
import SecureLogger from "../utils/secureLogger.js";
import API_CONFIG from '../config/api.js';
import useExpenseFormOptions from "./useExpenseFormOptions";
import ExpenseExportModal from "./components/ExpenseExportModal";
import ExpenseDocumentPreviewModal from "./components/ExpenseDocumentPreviewModal";
import { ExpenseFormModal, ExpenseDetailsModal } from "./components/ExpenseModals";
import RowActionsMenu from "../components/RowActionsMenu";
import { useTreasuryAccountSuggestion } from "../hooks/useCommercialTreasurySuggestion";
import { ExpenseDueDate, ExpenseIdentity, ExpensePaymentTag, ExpenseSummary, useExpenseWorkspaceStyle } from "./ExpenseWorkspace";
import { formatExpenseAmount, getExpensePaymentState, getExpensePrimaryAction, getExpenseRemaining, getExpenseDocuments } from "./expenseWorkspaceUtils";

const API_URL_EXPENSES = API_CONFIG.apiUrl("/expenses");
const API_URL_UPLOAD_DOCUMENT = API_CONFIG.apiUrl("/expenses/upload-document");
const API_URL_PAYMENT_MODES = API_CONFIG.apiUrl("/payment-modes");
const API_URL_PROCESS_EXPENSE_PAYMENT = API_CONFIG.apiUrl("/payments/process-expense-payment");
const API_URL_TREASURY_ACCOUNTS = API_CONFIG.apiUrl("/treasury/accounts");

// --- Permissions Keys ---
const DEFAULT_PERMISSIONS = {
  VIEW: "Gestion Commerciale.Depenses.SaisieDepenses.view",
  CREATE: "Gestion Commerciale.Depenses.SaisieDepenses.create",
  EDIT: "Gestion Commerciale.Depenses.SaisieDepenses.edit",
  DELETE: "Gestion Commerciale.Depenses.SaisieDepenses.delete",
  EXPORT: "Gestion Commerciale.Depenses.SaisieDepenses.export",
  CREATE_PAYMENT:
    "Gestion Commerciale.Depenses.SaisieDepenses.create_payment",
};
// -----------------------

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;

const generateIdempotencyKey = () => {
  const timestamp = new Date().getTime();
  const random = Math.floor(Math.random() * 1000000);
  return `${timestamp}-${random}`;
};

const GestionSaisieDepenses = ({
  moduleScope = "commercial",
  permissions = DEFAULT_PERMISSIONS,
  title = "Charges administratives",
}) => {
  const { selectedCompany, selectedWarehouse } = useSelection();
  const { user, hasPermission, isLoading: isLoadingAuth } = useAuth();
  const companyId = selectedCompany;
  const canView = hasPermission(permissions.VIEW);
  const canEdit = hasPermission(permissions.EDIT);
  const canPay = hasPermission(permissions.CREATE_PAYMENT);
  const workspaceStyle = useExpenseWorkspaceStyle();
  const screens = Grid.useBreakpoint();
  const filterId = useId();
  const scopeKey = `${companyId}:${selectedWarehouse}:${moduleScope}`;
  const scopeRef = useRef(scopeKey);
  scopeRef.current = scopeKey;
  const listRequest = useRef(null);
  const listDataScope = useRef(scopeKey);
  const paymentsRequest = useRef(null);
  const documentRequest = useRef(null);
  const [summary, setSummary] = useState(null);
  const [statusCounts, setStatusCounts] = useState(null);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [sort, setSort] = useState({ field: "date", order: "desc" });
  const [listError, setListError] = useState("");
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [recordingPayment, setRecordingPayment] = useState(false);
  const recordingPaymentRef = useRef(false);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true); // For table data
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const { categories, suppliers, loading: loadingDependencies, error: dependencyError,
    refresh: fetchDependencies, onChoiceOpen } = useExpenseFormOptions({
    userId: user?.id, companyId, warehouseId: selectedWarehouse, moduleScope,
    enabled: !isLoadingAuth && (canView || canEdit || hasPermission(permissions.CREATE)),
    formOpen: isModalVisible,
  });
  const [form] = Form.useForm();
  const [paymentForm] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const currentPage = pagination.current;
  const pageSize = pagination.pageSize;
  const [filters, setFilters] = useState({
    search: "",
    supplierId: null,
    categoryId: null,
    dateRange: null,
  });
  const [searchInput, setSearchInput] = useState(""); // Nouvel état pour la saisie dynamique
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [viewingExpense, setViewingExpense] = useState(null);
  const viewingExpenseRef = useRef(null);
  viewingExpenseRef.current = viewingExpense;
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [isPaymentModalVisible, setIsPaymentModalVisible] = useState(false);
  const [paymentExpense, setPaymentExpense] = useState(null);
  const [paymentModes, setPaymentModes] = useState([]);
  const [paymentModesLoading, setPaymentModesLoading] = useState(false);
  const [treasuryAccounts, setTreasuryAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const accountRequest = useRef(null);
  const paymentPreparation = useRef(0);
  const { onModeChange: suggestTreasuryAccount, onAccountChange: selectTreasuryAccount } =
    useTreasuryAccountSuggestion(paymentForm, {
      warehouseId: paymentExpense?.warehouse_id || selectedWarehouse,
      companyId: selectedCompany,
      moduleScope: moduleScope === "logistics" ? "logistics" : "commercial",
      enabled: isPaymentModalVisible && ["commercial", "logistics"].includes(moduleScope),
      referenceCurrencyOnly: moduleScope === "logistics",
      modeField: "payment_mode_id",
      accountField: "treasury_account_id",
      accounts: treasuryAccounts.filter((account) => !paymentExpense?.currency_id || Number(account.currency_id) === Number(paymentExpense.currency_id)),
    });
  const handledExpenseDeepLinkRef = useRef(null);
  useEffect(() => {
    accountRequest.current?.abort();
    paymentPreparation.current += 1;
    setIsPaymentModalVisible(false); setPaymentExpense(null); setTreasuryAccounts([]);
    paymentsRequest.current?.abort();
    documentRequest.current?.abort();
    setIsDetailsModalVisible(false); setViewingExpense(null); setPayments([]);
    setIsModalVisible(false); setEditingExpense(null); setUploadedDocuments([]);
    setUploading(false); uploadReservations.current = 0;
    uploadControllers.current.forEach((controller) => controller.abort());
    uploadControllers.current.clear();
    setMaxDocuments(1); setDocumentOptionsLoading(true); setDocumentOptionsError("");
    setIsExportModalVisible(false);
    setDocumentPreview({ open: false, loading: false, url: null, fileName: "", contentType: "", revokeUrl: false });
    setSearchInput("");
    setFilters({ search: "", supplierId: null, categoryId: null, dateRange: null });
    setPaymentStatusFilter("all"); setSort({ field: "date", order: "desc" });
    setPagination((prev) => ({ ...prev, current: 1, total: 0 }));
    setExpenses([]); setSummary(null); setStatusCounts(null);
    handledExpenseDeepLinkRef.current = null;
    form.resetFields(); paymentForm.resetFields();
    return () => accountRequest.current?.abort();
  }, [selectedCompany, selectedWarehouse, moduleScope, paymentForm, form]);
  
  // États pour la gestion des documents
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const uploadedDocumentsRef = useRef([]);
  uploadedDocumentsRef.current = uploadedDocuments;
  const uploadReservations = useRef(0);
  const uploadControllers = useRef(new Set());
  const previewUrls = useRef(new Set());
  const [maxDocuments, setMaxDocuments] = useState(1);
  const [documentOptionsLoading, setDocumentOptionsLoading] = useState(true);
  const [documentOptionsError, setDocumentOptionsError] = useState("");
  const [documentOptionsRetry, setDocumentOptionsRetry] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [documentPreview, setDocumentPreview] = useState({
    open: false,
    loading: false,
    url: null,
    fileName: "",
    contentType: "",
    revokeUrl: false,
  });

  // États pour l'export Excel
  const [isExportModalVisible, setIsExportModalVisible] = useState(false);

  useEffect(() => {
    const nextUrls = new Set(uploadedDocuments.map((document) => document.previewUrl).filter(Boolean));
    previewUrls.current.forEach((url) => { if (!nextUrls.has(url)) URL.revokeObjectURL(url); });
    previewUrls.current = nextUrls;
  }, [uploadedDocuments]);
  useEffect(() => () => {
    previewUrls.current.forEach((url) => URL.revokeObjectURL(url));
    uploadControllers.current.forEach((controller) => controller.abort());
  }, []);
  useEffect(() => {
    if (!isModalVisible) return undefined;
    const controller = new AbortController();
    setDocumentOptionsLoading(true); setDocumentOptionsError("");
    apiClient.get(`${API_URL_EXPENSES}/documents/options`, {
      signal: controller.signal, headers: { "X-Company-Id": companyId },
      params: { company_id: companyId, warehouse_id: selectedWarehouse, module_scope: moduleScope },
    }).then((response) => {
      if (!controller.signal.aborted) setMaxDocuments(response.data?.maxDocuments === 10 ? 10 : 1);
    }).catch(() => {
      if (!controller.signal.aborted) setDocumentOptionsError("Impossible de charger les options des justificatifs.");
    }).finally(() => { if (!controller.signal.aborted) setDocumentOptionsLoading(false); });
    return () => controller.abort();
  }, [isModalVisible, companyId, selectedWarehouse, moduleScope, documentOptionsRetry]);

  useEffect(() => {
    const previewUrl = documentPreview.revokeUrl
      ? documentPreview.url
      : null;
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [documentPreview.url, documentPreview.revokeUrl]);

  const fetchPaymentModes = async () => {
    if (!companyId) {
      setPaymentModes([]);
      return;
    }
    setPaymentModesLoading(true);
    const requestScope = scopeKey;
    try {
      const [companyRes, allRes] = await Promise.all([
        axios.get(API_URL_PAYMENT_MODES, {
          params: { company_id: companyId, limit: 200 },
        }),
        axios.get(API_URL_PAYMENT_MODES, { params: { limit: 200 } }),
      ]);
      const companyModes = Array.isArray(companyRes.data?.paymentModes)
        ? companyRes.data.paymentModes
        : [];
      const allModes = Array.isArray(allRes.data?.paymentModes)
        ? allRes.data.paymentModes
        : [];
      const genericModes = allModes.filter((mode) => !mode.company_id);
      const mergedModes = [...companyModes, ...genericModes];
      const uniqueModes = mergedModes.filter(
        (mode, index, self) => index === self.findIndex((m) => m.id === mode.id)
      );
      if (scopeRef.current !== requestScope) return;
      setPaymentModes(uniqueModes);
    } catch (error) {
      if (scopeRef.current !== requestScope) return;
      message.error("Erreur lors du chargement des modes de paiement.");
      setPaymentModes([]);
    } finally {
      if (scopeRef.current === requestScope) setPaymentModesLoading(false);
    }
  };

  const fetchTreasuryAccounts = async (warehouseIdOverride = null) => {
    const warehouseIdToUse = warehouseIdOverride || selectedWarehouse;
    if (!companyId || !warehouseIdToUse) {
      setTreasuryAccounts([]);
      return;
    }
    setAccountsLoading(true);
    accountRequest.current?.abort();
    const controller = new AbortController();
    accountRequest.current = controller;
    setTreasuryAccounts([]);
    try {
      const response = await axios.get(API_URL_TREASURY_ACCOUNTS, {
        signal: controller.signal,
        params: {
          company_id: companyId,
          warehouse_id: warehouseIdToUse,
          module_scope: moduleScope,
          include_inactive: false,
        },
      });
      if (controller.signal.aborted) return;
      if (response.data?.success) {
        const accounts = response.data.data || [];
        setTreasuryAccounts(accounts);
        if (!["commercial", "logistics"].includes(moduleScope) && !paymentForm.getFieldValue("treasury_account_id") && accounts.length) {
          const defaultAccount =
            accounts.find((account) => account.is_default === 1) || accounts[0];
          paymentForm.setFieldsValue({ treasury_account_id: defaultAccount.id });
        }
      } else {
        setTreasuryAccounts([]);
      }
    } catch (error) {
      if (controller.signal.aborted) return;
      message.error("Erreur lors du chargement des comptes de trésorerie.");
      setTreasuryAccounts([]);
    } finally {
      if (!controller.signal.aborted) setAccountsLoading(false);
    }
  };

  const buildExpensePaymentsUrl = (expenseId) => {
    const baseUrl = API_CONFIG.apiUrl(`/expenses/${expenseId}/payments`);
    const params = new URLSearchParams();
    if (companyId) {
      params.set("company_id", String(companyId));
    }
    if (moduleScope) {
      params.set("module_scope", moduleScope);
    }
    const query = params.toString();
    return query ? `${baseUrl}?${query}` : baseUrl;
  };

  const fetchExpensePayments = async (expenseId) => {
    if (!expenseId) return;
    paymentsRequest.current?.abort();
    const controller = new AbortController();
    paymentsRequest.current = controller;
    const requestScope = scopeKey;
    setPayments([]);
    setPaymentsLoading(true);
    try {
      const response = await apiClient.get(buildExpensePaymentsUrl(expenseId), {
        signal: controller.signal, headers: { "X-Company-Id": companyId },
      });
      if (controller.signal.aborted || scopeRef.current !== requestScope) return;
      const paymentsList = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.payments)
        ? response.data.payments
        : [];
      setPayments(paymentsList);
    } catch (error) {
      if (controller.signal.aborted || scopeRef.current !== requestScope) return;
      message.error("Erreur lors de la récupération des paiements.");
      setPayments([]);
    } finally {
      if (!controller.signal.aborted && scopeRef.current === requestScope) setPaymentsLoading(false);
    }
  };

  const refreshExpenseDetails = async (expenseId) => {
    if (!expenseId) return;
    const requestScope = scopeKey;
    try {
      const response = await apiClient.get(`${API_URL_EXPENSES}/${expenseId}`, {
        params: { companyId, module_scope: moduleScope }, headers: { "X-Company-Id": companyId },
      });
      if (response?.data && scopeRef.current === requestScope && Number(viewingExpenseRef.current?.id) === Number(expenseId)) {
        setViewingExpense(response.data);
      }
    } catch (error) {
      if (scopeRef.current === requestScope && Number(viewingExpenseRef.current?.id) === Number(expenseId)) {
        message.warning("Le paiement est enregistré, mais le détail de la charge n’a pas pu être actualisé.");
      }
    }
  };

  const fetchExpenses = useCallback(async () => {
    listRequest.current?.abort();
    if (isLoadingAuth || !companyId || !selectedWarehouse || !canView) {
      setExpenses([]); setSummary(null); setStatusCounts(null); setLoading(false); setListError("");
      setPagination((prev) => ({ ...prev, total: 0 }));
      return;
    }
    const controller = new AbortController();
    listRequest.current = controller;
    const requestScope = scopeKey;
    setLoading(true); setListError(""); setExpenses([]); setSummary(null); setStatusCounts(null);
    try {
      const params = {
        companyId, warehouseId: selectedWarehouse, page: currentPage, limit: pageSize,
        search: filters.search, module_scope: moduleScope, paymentStatus: paymentStatusFilter,
        sortField: sort.field, sortOrder: sort.order,
      };
      if (filters.supplierId) params.supplierId = filters.supplierId;
      if (filters.categoryId) params.categoryId = filters.categoryId;
      if (filters.dateRange?.length === 2 && filters.dateRange[0] && filters.dateRange[1]) {
        params.startDate = filters.dateRange[0].startOf("day").toISOString();
        params.endDate = filters.dateRange[1].endOf("day").toISOString();
      }
      const response = await apiClient.get(API_URL_EXPENSES, {
        params, signal: controller.signal, headers: { "X-Company-Id": companyId },
      });
      if (controller.signal.aborted || scopeRef.current !== requestScope) return;
      listDataScope.current = requestScope;
      const total = Number(response.data?.pagination?.totalItems || 0);
      const lastPage = Math.max(1, Math.ceil(total / pageSize));
      if (currentPage > lastPage) {
        setPagination((prev) => ({ ...prev, current: lastPage, total }));
        return;
      }
      setExpenses(Array.isArray(response.data?.expenses) ? response.data.expenses : []);
      setSummary(response.data?.summary || null);
      setStatusCounts(response.data?.statusCounts || null);
      setPagination((prev) => ({ ...prev, total }));
    } catch (error) {
      if (controller.signal.aborted || scopeRef.current !== requestScope) return;
      setListError(error.response?.data?.error || "Impossible de charger les charges administratives.");
      setExpenses([]); setSummary(null); setStatusCounts(null);
      setPagination((prev) => ({ ...prev, total: 0 }));
    } finally {
      if (!controller.signal.aborted && scopeRef.current === requestScope) setLoading(false);
    }
  }, [canView, companyId, selectedWarehouse, isLoadingAuth, scopeKey, filters, moduleScope,
    currentPage, pageSize, paymentStatusFilter, sort.field, sort.order]);

  useEffect(() => {
    fetchExpenses();
    return () => listRequest.current?.abort();
  }, [fetchExpenses]);

  useEffect(() => {
    if (!canView || filters.search === searchInput) return;
    const handler = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchInput }));
      setPagination((prev) => ({ ...prev, current: 1 }));
    }, 400);
    return () => clearTimeout(handler);
  }, [searchInput, canView, filters.search]);

  useEffect(() => () => { paymentsRequest.current?.abort(); documentRequest.current?.abort(); }, []);

  const handleTableChange = (nextPage, _tableFilters, nextSort) => {
    const field = nextSort?.order ? nextSort.field : "date";
    const order = nextSort?.order === "ascend" ? "asc" : "desc";
    setSort({ field, order });
    setPagination((prev) => ({ ...prev, current: field !== sort.field || order !== sort.order ? 1 : nextPage.current,
      pageSize: nextPage.pageSize }));
  };

  const handleSupplierFilterChange = (value) => {
    setFilters((prev) => ({ ...prev, supplierId: value || null }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleDateRangeChange = (range) => {
    setFilters((prev) => ({
      ...prev,
      dateRange: range && range.length === 2 ? range : null,
    }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setFilters({
      search: "",
      supplierId: null,
      categoryId: null,
      dateRange: null,
    });
    setPaymentStatusFilter("all");
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const showModal = (expense = null) => {
    const requiredPermission = expense ? permissions.EDIT : permissions.CREATE;
    if (!hasPermission(requiredPermission)) {
      message.warning(
        `Vous n'avez pas la permission de ${
          expense ? "modifier" : "créer"
        } des charges admin.`
      );
      return;
    }
    if (!selectedWarehouse) {
      message.warning(
        "Veuillez sélectionner un établissement avant d'ajouter ou modifier une charge admin."
      );
      return;
    }

    if (expense && (Number(expense.company_id) !== Number(companyId) ||
        Number(expense.warehouse_id) !== Number(selectedWarehouse))) {
      message.warning("Cette charge ne correspond pas à l’établissement sélectionné.");
      return;
    }
    if (expense && Number(expense.paid_amount || 0) > 0) {
      message.warning("Cette charge ne peut pas être modifiée après un paiement.");
      return;
    }
    setEditingExpense(expense);
    if (expense) {
      form.setFieldsValue({
        ...expense,
        warehouse_id: expense.warehouse_id || selectedWarehouse,
        date: expense.date ? dayjs(expense.date) : null,
        due_date: expense.due_date ? dayjs(expense.due_date) : null,
        supplier_id: expense.supplier_id || undefined,
      });
      
      setUploadedDocuments(getExpenseDocuments(expense));
    } else {
      form.setFieldsValue({
        expense_category_id: undefined,
        supplier_id: undefined,
        amount: null,
        date: dayjs(),
        due_date: dayjs(),
        bill: "",
        notes: "",
      });
      setUploadedDocuments([]);
    }
    setDocumentOptionsLoading(true);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    if (savingRef.current || uploading) return;
    closeDocumentPreview();
    setIsModalVisible(false);
    setEditingExpense(null);
    setUploadedDocuments([]);
    form.resetFields();
  };

  const persistExpense = async (values) => {
    if (savingRef.current || uploading || scopeRef.current !== scopeKey) return;
    savingRef.current = true; setSaving(true);
    const requestScope = scopeKey;
    try {
      const userIdToSubmit = user?.id || null;
      if (!userIdToSubmit) {
        message.error(
          "Erreur: ID utilisateur non trouvé. Veuillez vous reconnecter."
        );
        return;
      }

      const submissionData = {
        ...values,
        company_id: companyId,
        module_scope: moduleScope,
        warehouse_id: selectedWarehouse,
        user_id: userIdToSubmit,
        date: values.date ? values.date.toISOString() : null,
        due_date: values.due_date
          ? values.due_date.format("YYYY-MM-DD")
          : null,
        supplier_id: values.supplier_id || null,
        document_url: uploadedDocuments[0]?.url || null,
        document_name: uploadedDocuments[0]?.name || null,
        documents: uploadedDocuments.map((document) => ({ url: document.url, name: document.name })),
      };

      if (editingExpense) {
        await axios.put(
          `${API_URL_EXPENSES}/${editingExpense.id}`,
          submissionData
        );
        message.success("Charge admin mise à jour avec succès");
      } else {
        await axios.post(API_URL_EXPENSES, submissionData);
        message.success("Charge admin ajoutée avec succès");
      }
      if (scopeRef.current !== requestScope) return;
      if (hasPermission(permissions.VIEW)) fetchExpenses();
      setIsModalVisible(false); setEditingExpense(null); setUploadedDocuments([]); form.resetFields(); closeDocumentPreview();
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message;
      message.error("Erreur lors de la sauvegarde: " + errorMsg);
      SecureLogger.error("Failed to save expense");
    } finally {
      savingRef.current = false; setSaving(false);
    }
  };

  const handleOk = async () => {
    if (uploading || documentOptionsLoading || documentOptionsError || savingRef.current) return;
    const requestScope = scopeKey;
    const requiredPermission = editingExpense
      ? permissions.EDIT
      : permissions.CREATE;
    if (!hasPermission(requiredPermission)) {
      message.error("Action non autorisée.");
      return;
    }
    if (!selectedWarehouse) {
      message.error("Erreur: Aucun établissement sélectionné.");
      return;
    }

    let values;
    try {
      values = await form.validateFields();
    } catch (error) {
      if (!error?.errorFields) {
        message.error("Impossible de valider le formulaire.");
      }
      return;
    }

    if (scopeRef.current !== requestScope) return;
    if (!uploadedDocuments.length) {
      Modal.confirm({
        title: "Enregistrer sans pièce justificative ?",
        content:
          "La dépense restera autorisée, mais elle sera plus difficile à contrôler, à justifier et à valider en comptabilité. Ajoutez de préférence une facture, un reçu ou une autre preuve de la dépense.",
        okText: "Enregistrer quand même",
        cancelText: "Ajouter un justificatif",
        okButtonProps: { danger: true },
        centered: true,
        onOk: () => persistExpense(values),
      });
      return;
    }

    await persistExpense(values);
  };

  const handleDelete = async (id) => {
    if (!hasPermission(permissions.DELETE)) {
      message.error("Action non autorisée.");
      return;
    }
    try {
      await axios.delete(`${API_URL_EXPENSES}/${id}`, {
        params: { module_scope: moduleScope },
      });
      message.success("Charge admin supprimée avec succès");
      // Refetch only if user has view permission
      if (hasPermission(permissions.VIEW)) {
        fetchExpenses();
      }
    } catch (error) {
      message.error("Erreur lors de la suppression: " + error.message);
    }
  };

  const showDetailsModal = (expense) => {
    if (!canView) return;
    setViewingExpense(expense);
    setIsDetailsModalVisible(true);
    if (expense?.id) {
      fetchExpensePayments(expense.id);
    }
  };

  const handleCancelDetailsModal = () => {
    paymentsRequest.current?.abort();
    setIsDetailsModalVisible(false);
    setViewingExpense(null);
    setPayments([]);
  };

  // Fonctions pour l'export Excel
  const showExportModal = () => {
    if (!hasPermission(permissions.EXPORT)) {
      message.warning("Vous n'avez pas la permission d'exporter les charges admin.");
      return;
    }
    if (!selectedWarehouse) {
      message.warning("Veuillez sélectionner un établissement pour exporter les charges admin.");
      return;
    }
    setIsExportModalVisible(true);
  };

  const handleExportModalClose = () => {
    setIsExportModalVisible(false);
  };

  const openPaymentModal = async (expenseOverride = null) => {
    const targetExpense = expenseOverride?.id
      ? expenseOverride
      : viewingExpense;
    if (!targetExpense) return;
    if (getExpensePaymentState(targetExpense) === "Payé" || getExpenseRemaining(targetExpense) <= 0) {
      message.info("Cette charge n’a plus de solde à régler.");
      return;
    }
    if (!hasPermission(permissions.CREATE_PAYMENT)) {
      message.warning(
        "Vous n'avez pas la permission d'enregistrer un paiement sur cette dépense."
      );
      return;
    }
    if (!targetExpense.supplier_id) {
      message.warning(
        "Cette charge admin n'a pas de fournisseur. Paiement impossible."
      );
      return;
    }
    setPaymentExpense(targetExpense);
    setIsPaymentModalVisible(true);
    const preparation = ++paymentPreparation.current;
    await Promise.all([
      fetchPaymentModes(),
      fetchTreasuryAccounts(targetExpense?.warehouse_id || selectedWarehouse),
    ]);
    if (preparation !== paymentPreparation.current) return;
    const baseRemaining = Math.max(
      Number(targetExpense.amount || 0) -
        Number(targetExpense.paid_amount || 0),
      0
    );
    const initialDue =
      Number.isFinite(Number(targetExpense.due_amount)) &&
      Number(targetExpense.due_amount) > 0
        ? Number(targetExpense.due_amount)
        : baseRemaining;
    paymentForm.setFieldsValue({
      date: dayjs(),
      amount: initialDue,
      payment_mode_id: undefined,
      treasury_account_id: undefined,
      notes: "",
    });
  };

  useEffect(() => {
    if (
      isLoadingAuth ||
      !companyId ||
      !selectedWarehouse ||
      !hasPermission(permissions.VIEW)
    ) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const expenseId = Number(params.get("expense_id"));
    if (!Number.isInteger(expenseId) || expenseId <= 0) return;

    const deepLinkKey = `${companyId}:${selectedWarehouse}:${moduleScope}:${expenseId}:${
      params.get("action") || "view"
    }`;
    if (handledExpenseDeepLinkRef.current === deepLinkKey) return;
    const controller = new AbortController();
    const requestScope = scopeKey;

    const openDeepLinkedExpense = async () => {
      try {
        const response = await apiClient.get(`${API_URL_EXPENSES}/${expenseId}`, {
          params: { companyId, module_scope: moduleScope }, signal: controller.signal,
          headers: { "X-Company-Id": companyId },
        });
        if (controller.signal.aborted || scopeRef.current !== requestScope) return;
        const expense = response?.data;
        if (
          !expense ||
          Number(expense.company_id) !== Number(companyId) ||
          Number(expense.warehouse_id) !== Number(selectedWarehouse)
        ) {
          throw new Error(
            "Cette dépense ne correspond pas à l'établissement sélectionné."
          );
        }

        handledExpenseDeepLinkRef.current = deepLinkKey;
        showDetailsModal(expense);
        if (
          params.get("action") === "pay" &&
          hasPermission(permissions.CREATE_PAYMENT)
        ) {
          await openPaymentModal(expense);
        }
      } catch (error) {
        if (controller.signal.aborted || scopeRef.current !== requestScope) return;
        handledExpenseDeepLinkRef.current = null;
        message.error(
          error?.response?.data?.error ||
            error?.message ||
            "Impossible d'ouvrir cette dépense."
        );
      }
    };

    openDeepLinkedExpense();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyId,
    selectedWarehouse,
    isLoadingAuth,
    hasPermission,
    moduleScope,
    scopeKey,
    permissions.VIEW,
    permissions.CREATE_PAYMENT,
  ]);

  const closePaymentModal = () => {
    paymentPreparation.current += 1;
    accountRequest.current?.abort();
    setIsPaymentModalVisible(false);
    setPaymentExpense(null);
    paymentForm.resetFields();
  };

  const handlePaymentSubmit = async () => {
    if (!paymentExpense || recordingPaymentRef.current) return;
    if (!hasPermission(permissions.CREATE_PAYMENT)) {
      message.error(
        "Vous n'avez pas la permission d'enregistrer ce paiement."
      );
      return;
    }
    recordingPaymentRef.current = true; setRecordingPayment(true);
    const requestScope = scopeKey;
    try {
      const values = await paymentForm.validateFields();
      if (scopeRef.current !== requestScope) return;
      const idempotencyKey = generateIdempotencyKey();

      const payload = {
        expense_id: paymentExpense.id,
        company_id: companyId,
        warehouse_id: paymentExpense.warehouse_id || selectedWarehouse,
        payment_mode_id: values.payment_mode_id,
        amount: values.amount,
        date: values.date ? values.date.toISOString() : null,
        notes: values.notes || "",
        treasury_account_id: values.treasury_account_id,
        module_scope: moduleScope,
        staff_user_id: user?.id || null,
      };

      await axios.post(API_URL_PROCESS_EXPENSE_PAYMENT, payload, {
        headers: {
          "X-Idempotency-Key": idempotencyKey,
        },
      });

      if (scopeRef.current !== requestScope) return;
      message.success("Paiement enregistré avec succès");
      await fetchExpensePayments(paymentExpense.id);
      await refreshExpenseDetails(paymentExpense.id);
      if (hasPermission(permissions.VIEW)) {
        fetchExpenses();
      }
      closePaymentModal();
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message;
      if (!error.errorFields && scopeRef.current === requestScope) message.error(`Erreur lors du paiement: ${errorMsg}`);
    } finally {
      recordingPaymentRef.current = false; setRecordingPayment(false);
    }
  };

  const buildExpenseDocumentUrl = (expenseId, documentIndex = 0) => {
    const baseUrl = API_CONFIG.apiUrl(`/expenses/${expenseId}/document`);
    const params = new URLSearchParams();
    if (companyId) {
      params.set("company_id", String(companyId));
    }
    if (moduleScope) {
      params.set("module_scope", moduleScope);
    }
    params.set("document_index", String(documentIndex));
    params.set("warehouse_id", String(selectedWarehouse));
    const query = params.toString();
    return query ? `${baseUrl}?${query}` : baseUrl;
  };

  const openExpenseDocument = async (expenseId, fileName, documentIndex = 0) => {
    documentRequest.current?.abort();
    const controller = new AbortController();
    documentRequest.current = controller;
    const requestScope = scopeKey;
    setDocumentPreview({
      open: true,
      loading: true,
      url: null,
      fileName: fileName || "Pièce justificative",
      contentType: "",
      revokeUrl: false,
    });
    try {
      const response = await axios.get(buildExpenseDocumentUrl(expenseId, documentIndex), {
        responseType: "blob",
        signal: controller.signal,
        headers: { "X-Company-Id": companyId },
      });
      if (controller.signal.aborted || scopeRef.current !== requestScope) return;
      const blobUrl = window.URL.createObjectURL(response.data);
      setDocumentPreview({
        open: true,
        loading: false,
        url: blobUrl,
        fileName: fileName || "Pièce justificative",
        contentType:
          response.headers?.["content-type"] || response.data?.type || "",
        revokeUrl: true,
      });
    } catch (error) {
      if (controller.signal.aborted || scopeRef.current !== requestScope) return;
      setDocumentPreview((current) => ({
        ...current,
        open: false,
        loading: false,
      }));
      const errorMsg = error.response?.data?.error || error.message;
      message.error(`Erreur lors de l'ouverture du document: ${errorMsg}`);
    }
  };

  const closeDocumentPreview = () => {
    documentRequest.current?.abort();
    setDocumentPreview({
      open: false,
      loading: false,
      url: null,
      fileName: "",
      contentType: "",
      revokeUrl: false,
    });
  };

  // Fonction pour gérer l'upload de document
  const handleDocumentUpload = async (file) => {
    if (documentOptionsLoading || documentOptionsError || savingRef.current) return false;
    if (!["application/pdf", "image/jpeg", "image/png"].includes(file.type) || file.size > 10 * 1024 * 1024) {
      message.error("Choisissez un fichier PDF, JPEG ou PNG de 10 Mo maximum.");
      return false;
    }
    if (uploadedDocumentsRef.current.length + uploadReservations.current >= maxDocuments) {
      message.warning(`La charge accepte ${maxDocuments} justificatif(s) maximum.`);
      return false;
    }
    const requestScope = scopeKey;
    const controller = new AbortController();
    uploadControllers.current.add(controller);
    uploadReservations.current += 1; setUploading(true);
    const formData = new FormData();
    formData.append('document', file);
    formData.append('module_scope', moduleScope);
    try {
      const response = await axios.post(API_URL_UPLOAD_DOCUMENT, formData, {
        signal: controller.signal, headers: { 'X-Company-Id': companyId },
        params: { company_id: companyId, warehouse_id: selectedWarehouse, module_scope: moduleScope },
      });
      if (controller.signal.aborted || scopeRef.current !== requestScope) return false;
      if (!response.data?.success || !response.data.url) throw new Error("Le justificatif n’a pas pu être joint.");
      const previewUrl = URL.createObjectURL(file);
      const nextDocuments = [...uploadedDocumentsRef.current, { url: response.data.url, name: response.data.originalName,
        key: response.data.key, previewUrl, contentType: file.type }];
      uploadedDocumentsRef.current = nextDocuments;
      setUploadedDocuments(nextDocuments);
      message.success('Justificatif joint avec succès');
    } catch (error) {
      if (!controller.signal.aborted && scopeRef.current === requestScope) message.error(error.response?.data?.error || "Impossible de joindre le justificatif.");
    } finally {
      uploadControllers.current.delete(controller);
      if (scopeRef.current === requestScope) {
        uploadReservations.current = Math.max(0, uploadReservations.current - 1);
        setUploading(uploadReservations.current > 0);
      }
    }
    return false;
  };

  const handleDocumentRemove = (document) => {
    if (uploading || savingRef.current) return;
    closeDocumentPreview();
    const nextDocuments = uploadedDocumentsRef.current.filter((current) => current !== document);
    uploadedDocumentsRef.current = nextDocuments;
    setUploadedDocuments(nextDocuments);
  };

  const handleUploadedDocumentOpen = (document, index) => {
    if (document.previewUrl) {
      setDocumentPreview({ open: true, loading: false, url: document.previewUrl,
        fileName: document.name, contentType: document.contentType || "", revokeUrl: false });
    } else if (editingExpense?.id) {
      const storedIndex = getExpenseDocuments(editingExpense).findIndex((current) => current.url === document.url);
      openExpenseDocument(editingExpense.id, document.name, storedIndex >= 0 ? storedIndex : index);
    }
  };

  const formatAmount = formatExpenseAmount;

  const renderExpenseActions = (record) => {
    const hasPayments = Number(record.paid_amount || 0) > 0;
    const items = [{ key: "view", icon: <EyeOutlined />, label: "Voir le détail",
      onClick: () => showDetailsModal(record) }];
    if (canEdit) items.push({
      key: "edit", icon: <EditOutlined />,
      label: hasPayments ? "Modification indisponible — paiements enregistrés" : "Modifier",
      disabled: hasPayments, onClick: () => showModal(record),
    });
    if (hasPermission(permissions.DELETE)) items.push({ type: "divider" }, {
      key: "delete", icon: <DeleteOutlined />,
      label: hasPayments ? "Suppression indisponible — paiements enregistrés" : "Supprimer",
      disabled: hasPayments, danger: !hasPayments,
      onClick: () => Modal.confirm({
        title: "Supprimer cette charge administrative ?", okText: "Supprimer", cancelText: "Annuler",
        okButtonProps: { danger: true }, onOk: () => handleDelete(record.id),
      }),
    });
    const action = getExpensePrimaryAction(record, { canPay, canEdit });
    const label = action === "payment" ? "Enregistrer" : action === "complete" ? "Compléter" : "Voir";
    const help = action === "payment" ? "Enregistrer un paiement" : action === "complete"
      ? "Renseigner le fournisseur pour pouvoir enregistrer un paiement" : "Voir le détail de la charge";
    return <div className="expense-workspace__row-actions">
      <Tooltip title={help}>
        <Button color={action === "view" ? "default" : "primary"} variant="outlined"
          onClick={() => action === "payment" ? openPaymentModal(record) :
          action === "complete" ? showModal(record) : showDetailsModal(record)}
          aria-label={`${help} : ${record.bill || record.category_name || record.id}`}>
          {label}
        </Button>
      </Tooltip>
      <RowActionsMenu items={items} ariaLabel={`Afficher les actions de la charge admin ${record.bill || record.id}`} />
    </div>;
  };

  const columnSort = (field) => ({ sorter: true,
    sortOrder: sort.field === field ? (sort.order === "asc" ? "ascend" : "descend") : null });
  const columns = [
    { title: "Date", dataIndex: "date", key: "date", width: 110, ...columnSort("date"),
      render: (value) => <span className="expense-workspace__date">{value && dayjs(value).isValid()
        ? dayjs(value).format("DD/MM/YYYY") : "—"}</span> },
    { title: "Charge / Fournisseur", key: "charge",
      render: (_, record) => <ExpenseIdentity expense={record} onDocument={openExpenseDocument} /> },
    { title: "Échéance", dataIndex: "due_date", key: "due_date", width: 125, ...columnSort("due_date"),
      render: (_, record) => <ExpenseDueDate expense={record} asOfDate={summary?.as_of_date} /> },
    { title: "Montant", dataIndex: "amount", key: "amount", width: 125, align: "right", ...columnSort("amount"),
      render: (value) => <span className="expense-workspace__money">{formatAmount(value)}</span> },
    { title: "Payé", dataIndex: "paid_amount", key: "paid_amount", width: 125, align: "right", ...columnSort("paid_amount"),
      render: (value) => <span className="expense-workspace__money">{formatAmount(value ?? 0)}</span> },
    { title: "Reste à payer", dataIndex: "remaining_amount", key: "remaining_amount", width: 140,
      align: "right", ...columnSort("remaining_amount"),
      render: (_, record) => <span className="expense-workspace__money">{formatAmount(getExpenseRemaining(record))}</span> },
    { title: "Statut", key: "payment_status", width: 190,
      render: (_, record) => <ExpensePaymentTag expense={record} /> },
    { title: "Actions", key: "actions", fixed: "right", width: 170, align: "right",
      render: (_, record) => renderExpenseActions(record) },
  ];

  const paymentColumns = [
    {
      title: "Date",
      dataIndex: "payment_date",
      key: "payment_date",
      render: (text, record) =>
        dayjs(record.payment_date || record.date).isValid()
          ? dayjs(record.payment_date || record.date).format("DD/MM/YYYY")
          : "-",
    },
    {
      title: "Mode",
      dataIndex: "payment_mode_name",
      key: "payment_mode_name",
      render: (text) => text || "-",
    },
    {
      title: "Référence",
      dataIndex: "payment_number",
      key: "payment_number",
      render: (text) => text || "-",
    },
    {
      title: "Montant",
      dataIndex: "payment_amount",
      key: "payment_amount",
      align: "right",
      render: (text, record) => formatAmount(text ?? record.amount),
    },
    {
      title: "Notes",
      dataIndex: "remarks",
      key: "remarks",
      render: (text, record) => text || record.notes || "-",
    },
  ];

  // Render logic based on permissions loading and view permission
  if (isLoadingAuth) {
    return <Spin tip="Chargement de l'authentification..." />;
  }

  if (!hasPermission(permissions.VIEW)) {
    return (
      <Card title={title}>
        <Alert
          message="Accès non autorisé"
          description="Vous n'avez pas les permissions nécessaires pour voir cette section."
          type="error"
          showIcon
        />
      </Card>
    );
  }

  const rawPaidAmount = viewingExpense ? Number(viewingExpense.paid_amount || 0) : 0;
  const rawTotalAmount = viewingExpense ? Number(viewingExpense.amount || 0) : 0;
  const viewingIsPaid =
    viewingExpense &&
    (viewingExpense.payment_status === "Payé" ||
      (rawTotalAmount > 0 && rawPaidAmount >= rawTotalAmount));
  const canCreatePayment = hasPermission(permissions.CREATE_PAYMENT);
  const canAddPayment =
    viewingExpense &&
    !viewingIsPaid &&
    getExpenseRemaining(viewingExpense) > 0 &&
    viewingExpense.supplier_id &&
    canCreatePayment;
  const paymentMaxAmount = paymentExpense
    ? Number.isFinite(Number(paymentExpense.due_amount)) && Number(paymentExpense.due_amount) > 0
      ? Number(paymentExpense.due_amount)
      : Math.max(
          Number(paymentExpense.amount || 0) - Number(paymentExpense.paid_amount || 0),
          0
        )
    : undefined;

  const visibleExpenses = listDataScope.current === scopeKey ? expenses : [];
  const visibleSummary = listDataScope.current === scopeKey ? summary : null;
  const visibleCounts = listDataScope.current === scopeKey ? statusCounts : null;
  const paymentTabs = [
    ["all", "Toutes"], ["unpaid", "Non payées"], ["partial", "Partielles"], ["paid", "Payées"], ["overdue", "En retard"],
  ].map(([key, label]) => ({ key, label: `${label}${visibleCounts ? ` (${visibleCounts[key]})` : loading ? " (…)" : ""}` }));
  const paginationProps = { ...pagination, showSizeChanger: true, pageSizeOptions: [10, 20, 50, 100],
    showTotal: (total, range) => `Affichage de ${range[0]} à ${range[1]} sur ${total} résultat${total === 1 ? "" : "s"}` };

  return (
    <section className="expense-workspace" style={workspaceStyle} aria-label={title}>
      <header className="expense-workspace__header">
        <div><h1>{title}</h1><p>Suivez vos dépenses, vos échéances et vos règlements.</p></div>
        <div className="expense-workspace__header-actions" role="group" aria-label="Actions des charges">
          {hasPermission(permissions.EXPORT) && <Button aria-label="Exporter Excel" icon={<DownloadOutlined />} onClick={showExportModal}
            disabled={!selectedWarehouse}>Exporter Excel</Button>}
          {hasPermission(permissions.CREATE) && <Button aria-label="Nouvelle charge" type="primary" icon={<PlusOutlined />}
            onClick={() => showModal()} disabled={!selectedWarehouse}>Nouvelle charge</Button>}
        </div>
      </header>
      {!selectedWarehouse && <Alert type="info" showIcon message="Sélectionnez un établissement pour consulter les charges."
        style={{ marginBottom: 16 }} />}
      <ExpenseSummary summary={visibleSummary} loading={loading} />
      {listError && <Alert type="error" showIcon message={listError}
        action={<Button onClick={fetchExpenses}>Réessayer</Button>} style={{ marginBottom: 16 }} />}
      {dependencyError && <Alert type="warning" showIcon message={dependencyError}
        action={<Button onClick={fetchDependencies}>Réessayer les filtres</Button>} style={{ marginBottom: 16 }} />}
      <div className="expense-workspace__filters" role="search" aria-label="Filtres des charges">
        <div className="expense-workspace__field">
          <label htmlFor={`${filterId}-search`}>Recherche</label>
          <Input id={`${filterId}-search`} placeholder="Facture, référence, catégorie, fournisseur…"
            prefix={<SearchOutlined />} allowClear value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)} disabled={!selectedWarehouse} />
        </div>
        <div className="expense-workspace__field">
          <label htmlFor={`${filterId}-supplier`}>Fournisseur</label>
          <Select id={`${filterId}-supplier`} placeholder="Tous les fournisseurs" allowClear showSearch
            value={filters.supplierId || undefined} loading={loadingDependencies} disabled={!selectedWarehouse}
            onDropdownVisibleChange={onChoiceOpen}
            onChange={handleSupplierFilterChange} optionFilterProp="label"
            options={suppliers.map((supplier) => ({ label: supplier.name, value: supplier.id }))} />
        </div>
        <div className="expense-workspace__field">
          <label htmlFor={`${filterId}-category`}>Catégorie</label>
          <Select id={`${filterId}-category`} placeholder="Toutes les catégories" allowClear showSearch
            value={filters.categoryId || undefined} loading={loadingDependencies} disabled={!selectedWarehouse}
            onDropdownVisibleChange={onChoiceOpen}
            onChange={(value) => { setFilters((prev) => ({ ...prev, categoryId: value || null }));
              setPagination((prev) => ({ ...prev, current: 1 })); }}
            optionFilterProp="label" options={categories.map((category) => ({ label: category.name, value: category.id }))} />
        </div>
        <div className="expense-workspace__field">
          <label htmlFor={`${filterId}-period`}>Période</label>
          <RangePicker id={`${filterId}-period`} format="DD/MM/YYYY" value={filters.dateRange || null}
            onChange={handleDateRangeChange} disabled={!selectedWarehouse}
            disabledDate={(current) => current && current > dayjs().endOf("day")} placeholder={["Début", "Fin"]} />
        </div>
        <Button aria-label="Réinitialiser" icon={<ReloadOutlined />} onClick={handleClearFilters} disabled={!selectedWarehouse}>Réinitialiser</Button>
      </div>
      <Tabs className="expense-workspace__tabs" activeKey={paymentStatusFilter} items={paymentTabs}
        onChange={(key) => { setPaymentStatusFilter(key); setPagination((prev) => ({ ...prev, current: 1 })); }} />
      <div className="expense-workspace__table" role="region" aria-label="Liste des charges administratives" aria-busy={loading}>
        {screens.lg !== false ? <Table columns={columns} dataSource={visibleExpenses} rowKey="id" loading={loading}
          pagination={paginationProps} onChange={handleTableChange} scroll={{ x: 1220 }}
          locale={{ emptyText: listError ? "Chargement indisponible" : <Empty description={selectedWarehouse
            ? "Aucune charge ne correspond aux filtres." : "Sélectionnez un établissement."} /> }} /> :
          <Spin spinning={loading}>
            <div className="expense-workspace__cards">
              {visibleExpenses.map((expense) => <article className="expense-workspace__card" key={expense.id}>
                <div className="expense-workspace__card-header">
                  <ExpenseIdentity expense={expense} onDocument={openExpenseDocument} /><ExpensePaymentTag expense={expense} />
                </div>
                <div className="expense-workspace__card-dates">
                  <div><small className="expense-workspace__muted">Date de saisie</small><div className="expense-workspace__date">
                    {expense.date && dayjs(expense.date).isValid() ? dayjs(expense.date).format("DD/MM/YYYY") : "—"}</div></div>
                  <div><small className="expense-workspace__muted">Échéance</small>
                    <ExpenseDueDate expense={expense} asOfDate={summary?.as_of_date} /></div>
                </div>
                <dl className="expense-workspace__card-amounts">
                  <div><dt>Montant</dt><dd>{formatAmount(expense.amount)}</dd></div>
                  <div><dt>Payé</dt><dd>{formatAmount(expense.paid_amount ?? 0)}</dd></div>
                  <div><dt>Reste à payer</dt><dd>{formatAmount(getExpenseRemaining(expense))}</dd></div>
                </dl>
                <div className="expense-workspace__card-footer">{renderExpenseActions(expense)}</div>
              </article>)}
              {!loading && !visibleExpenses.length && <Empty description={listError ? "Chargement indisponible" : "Aucune charge ne correspond aux filtres."} />}
            </div>
            <div className="expense-workspace__pagination"><Pagination {...paginationProps}
              onChange={(current, pageSize) => setPagination((prev) => ({ ...prev, current, pageSize }))} /></div>
          </Spin>}
      </div>
      <p className="expense-workspace__footer">
        {summary?.as_of_date ? `Échéances au ${dayjs(summary.as_of_date).format("DD/MM/YYYY")} · synthèse de tous les statuts pour les filtres sélectionnés.` :
          !loading && !listError && selectedWarehouse ? "Synthèse indisponible." : ""}
      </p>
      {isModalVisible && <ExpenseFormModal open form={form} expense={editingExpense}
        establishment={editingExpense?.warehouse_name || user?.assigned_warehouses?.find((warehouse) => Number(warehouse.id) === Number(selectedWarehouse))?.name}
        categories={categories} suppliers={suppliers} loadingDependencies={loadingDependencies}
        dependencyError={dependencyError} onRetryDependencies={fetchDependencies} onChoiceOpen={onChoiceOpen}
        documents={uploadedDocuments} uploading={uploading} maxDocuments={maxDocuments}
        documentOptionsLoading={documentOptionsLoading} documentOptionsError={documentOptionsError}
        onRetryDocumentOptions={() => setDocumentOptionsRetry((value) => value + 1)}
        saving={saving} onSave={handleOk} onCancel={handleCancel}
        onUpload={handleDocumentUpload} onRemove={handleDocumentRemove} onPreview={handleUploadedDocumentOpen} />}
      {viewingExpense && <ExpenseDetailsModal key={viewingExpense.id} open={isDetailsModalVisible} expense={viewingExpense}
        companyId={companyId} moduleScope={moduleScope} payments={payments} paymentColumns={paymentColumns}
        paymentsLoading={paymentsLoading} canAddPayment={canAddPayment}
        paymentDisabledReason={!canCreatePayment ? "Permission de création de paiement requise." : !viewingExpense.supplier_id
          ? "Associez un fournisseur pour enregistrer un paiement." : "Cette charge est déjà réglée."}
        onPayment={() => openPaymentModal()} onClose={handleCancelDetailsModal} />}

      <Modal
        title="Ajouter un paiement"
        open={isPaymentModalVisible}
        onOk={handlePaymentSubmit}
        confirmLoading={recordingPayment}
        closable={!recordingPayment}
        maskClosable={!recordingPayment}
        keyboard={!recordingPayment}
        onCancel={() => { if (!recordingPayment) closePaymentModal(); }}
        okText="Enregistrer"
        cancelText="Annuler"
        destroyOnClose
      >
        <Form form={paymentForm} layout="vertical">
          {paymentExpense && <p>
            <Text strong>{paymentExpense.bill || paymentExpense.category_name || "Charge administrative"}</Text>
            {" · "}{paymentExpense.supplier_name || "Fournisseur"}<br />
            Solde à régler : <Text strong>{formatAmount(getExpenseRemaining(paymentExpense))}</Text>
          </p>}
          {treasuryAccounts.length === 0 && (
            <Alert
              message="Aucun compte de trésorerie"
              description="Créez un compte de trésorerie pour enregistrer ce paiement."
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="date"
                label="Date de paiement"
                rules={[{ required: true, message: "Veuillez choisir la date!" }]}
              >
                <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="amount"
                label="Montant"
                rules={[
                  { required: true, message: "Veuillez saisir le montant!" },
                  {
                    type: "number",
                    min: 0.01,
                    message: "Le montant doit être positif.",
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  precision={2}
                  min={0.01}
                  max={paymentMaxAmount}
                  addonAfter="FCFA"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="treasury_account_id"
                label="Compte de trésorerie"
                rules={[
                  {
                    required: true,
                    message: "Veuillez sélectionner un compte de trésorerie",
                  },
                ]}
              >
                <Select
                  placeholder="Sélectionner un compte"
                  loading={accountsLoading}
                  disabled={treasuryAccounts.length === 0}
                  onChange={selectTreasuryAccount}
                >
                  {treasuryAccounts.map((account) => (
                    <Option key={account.id} value={account.id}>
                      {account.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="payment_mode_id"
                label="Mode de paiement"
                rules={[
                  {
                    required: true,
                    message: "Veuillez sélectionner un mode de paiement",
                  },
                ]}
              >
                <Select
                  placeholder="Sélectionner un mode"
                  loading={paymentModesLoading}
                  onChange={["commercial", "logistics"].includes(moduleScope) ? suggestTreasuryAccount : undefined}
                >
                  {paymentModes.map((mode) => (
                    <Option key={mode.id} value={mode.id}>
                      {mode.name}
                      {mode.company_id ? " [Spécifique]" : " [Général]"}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item name="notes" label="Notes">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* Modal d'Export Excel */}
      <ExpenseExportModal
        visible={isExportModalVisible}
        onClose={handleExportModalClose}
        moduleScope={moduleScope}
        existingFilters={{
          warehouseId: selectedWarehouse,
          search: filters.search,
          supplierIds: filters.supplierId ? [filters.supplierId] : [],
          categoryIds: filters.categoryId ? [filters.categoryId] : [],
          dateRange: filters.dateRange,
        }}
      />
      <ExpenseDocumentPreviewModal
        open={documentPreview.open}
        loading={documentPreview.loading}
        url={documentPreview.url}
        fileName={documentPreview.fileName}
        contentType={documentPreview.contentType}
        onClose={closeDocumentPreview}
      />
    </section>
  );
};

export default GestionSaisieDepenses;
